import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes, faSliders } from '@fortawesome/free-solid-svg-icons';

import type { StripeCheckoutMetadataEntry } from '@/@types/graphql-types';
import type { Device } from '@/helpers/composables/useUserAgent';
import type { SubscriptionInfo, SubscriptionTitles } from '@/features/jw-pro/types';

// Shared Stripe products per price point, not per variant — reporting/attribution for which.
type PricingExpSubscriptionTitles =
	| 'proPricingExpControl'
	| 'proPricingExpYearlyV1'
	| 'proPricingExpYearlyV2'
	| 'proPricingExpLifetimeV1'
	| 'proPricingExpLifetimeV2'
	| 'proPricingExpLegacyVariant1Monthly'
	| 'proPricingExpLegacyVariant2Monthly'
	| 'proPricingExpLegacyVariant3Monthly'
	| 'proPricingExpLegacyVariant4Monthly'
	| 'proPricingExpLegacyVariant5Monthly'
	| 'proPricingExpLegacyVariant5Yearly'
	| 'proPricingExpLegacyVariant5Lifetime'
	| 'proPricingExpLegacyVariant6Monthly';

export const PRICING_EXP_SUBSCRIPTIONS: Record<
	Extract<SubscriptionTitles, PricingExpSubscriptionTitles>,
	SubscriptionInfo
> = {
	// Monthly (3.99) — shared by control and every variant's monthly plan
	proPricingExpControl: {
		id: 'prod_UwE42g3VLRPNQ1',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLc1KD0TTmBHo0FMOZ8kNM', price: 3.99, currency: 'EUR' },
		},
	},
	// Yearly (9.99) — shared by variant_1 and variant_3
	proPricingExpYearlyV1: {
		id: 'prod_UwE66Iew26TAGF',
		type: 'regular',
		timeframe: 'yearly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLeRKD0TTmBHo0fXfKV6IX', price: 9.99, currency: 'EUR' },
		},
	},
	// Yearly (15.99) — variant_4 only
	proPricingExpYearlyV2: {
		id: 'prod_VLGF2bFoECRVht',
		type: 'regular',
		timeframe: 'yearly',
		prices: {
			DEFAULT: { priceId: 'price_1UKZjKKD0TTmBHo0W901Etm8', price: 15.99, currency: 'EUR' },
		},
	},
	// Lifetime (24.99) — shared by variant_2 and variant_3
	proPricingExpLifetimeV1: {
		id: 'prod_UwE9BH245yc49C',
		type: 'regular',
		timeframe: 'one time',
		prices: {
			DEFAULT: { priceId: 'price_1TwLheKD0TTmBHo0tYRmiONP', price: 24.99, currency: 'EUR' },
		},
	},
	// Lifetime (39.99) — variant_4 only
	proPricingExpLifetimeV2: {
		id: 'prod_VLGHDxuD4YYTbZ',
		type: 'regular',
		timeframe: 'one time',
		prices: {
			DEFAULT: { priceId: 'price_1UKZkkKD0TTmBHo0MNqoeaKe', price: 39.99, currency: 'EUR' },
		},
	},
	// Legacy products from the pre-rename EXP_JW_PRO_PRICING rollout (variant_1–variant_6).
	// Not referenced by any current variant/plan — kept only so findSubscriptionBy() can still
	// resolve existing subscribers who checked out under these product IDs before the experiment
	// was renamed to EXP_JW_PRO_PRICING_V2 and consolidated onto shared products above.
	proPricingExpLegacyVariant1Monthly: {
		id: 'prod_UwE5FOKO3TSBOQ',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLctKD0TTmBHo0XPkPNEIV', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant2Monthly: {
		id: 'prod_UwE5J06B2FaYEc',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLdEKD0TTmBHo0QAeSIMJ8', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant3Monthly: {
		id: 'prod_UwE5q5evVDUSBa',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLdiKD0TTmBHo0dlxFFJqH', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant4Monthly: {
		id: 'prod_UwE9jZTQonKoTx',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLh0KD0TTmBHo0Gtnc17CJ', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant5Monthly: {
		id: 'prod_UwEAoiUNal05cq',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLi4KD0TTmBHo01jBJpu0a', price: 0.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant5Yearly: {
		id: 'prod_UwEAes8Y9vjcy5',
		type: 'regular',
		timeframe: 'yearly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLiSKD0TTmBHo0AzxDbowX', price: 9.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant5Lifetime: {
		id: 'prod_UwEBk7eLmiNKYG',
		type: 'regular',
		timeframe: 'one time',
		prices: {
			DEFAULT: { priceId: 'price_1TwLj6KD0TTmBHo0FNV4XOwc', price: 24.99, currency: 'EUR' },
		},
	},
	proPricingExpLegacyVariant6Monthly: {
		id: 'prod_UwECjbLhQbmIgp',
		type: 'regular',
		timeframe: 'monthly',
		prices: {
			DEFAULT: { priceId: 'price_1TwLjjKD0TTmBHo0yt2o5Vho', price: 0.99, currency: 'EUR' },
		},
	},
};
// EXP_JW_PRO_PRICING_V2

export type PlanKey = 'monthly' | 'yearly' | 'lifetime';

type Perk = {
	icon?: string;
	faIcon?: IconDefinition;
	titleKey: string;
	titleParams?: Record<string, string>;
	textKey: string;
	// hardcoded since the translation for WEBAPP_PRO_MODAL_BANNERS_SUBHEAD is missing its full stop
	textSuffix?: string;
};

type VariantSpec = {
	titleKey: string;
	badgeText?: string;
	tagline?: string;
	logoOverride?: string;
	ctaProvider?: string;
	perks: Perk[];
	showPlanSelector?: boolean;
	defaultPlanKey?: PlanKey;
	showYearlyOption?: boolean;
	showLifetimeOption?: boolean;
	showOriginalPrice?: boolean;
	monthlyPriceKey: SubscriptionTitles;
	yearlyPriceKey?: SubscriptionTitles;
	lifetimePriceKey?: SubscriptionTitles;
};

// Shared perks for variant_1 through variant_5 — only variant_6 swaps the second perk
export const SHARED_PERKS: Perk[] = [
	{
		icon: `${ASSETS_DIR}/img/experiments/jw-pro-pricing/jwtv-free-badge.svg`,
		titleKey: 'WEBAPP_WATCH_FREE_ON_PROVIDER',
		titleParams: { provider: 'JustWatch TV' },
		textKey: 'WEBAPP_PRO_MODAL_FREE_AND_RENTALS',
	},
	{
		faIcon: faSliders,
		titleKey: 'WEBAPP_PRO_MODAL_WATCHLIST_FILTERS_HEAD',
		textKey: 'WEBAPP_PRO_MODAL_WATCHLIST_FILTERS_SUBHEAD',
	},
	{
		faIcon: faTimes,
		titleKey: 'WEBAPP_PRO_MODAL_BANNERS_HEAD',
		textKey: 'WEBAPP_PRO_MODAL_BANNERS_SUBHEAD',
		textSuffix: '.',
	},
];

// Copy per variant — pricing is always derived from the real SUBSCRIPTIONS constants in
// JwProPricingExpModal.vue, never hardcoded here.
export const VARIANT_SPECS: Partial<Record<string, VariantSpec>> = {
	// v1: monthly/yearly plan selector — Monthly (3.99), Yearly (9.99)
	variant_1: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showYearlyOption: true,
		defaultPlanKey: 'yearly',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpControl',
		yearlyPriceKey: 'proPricingExpYearlyV1',
	},
	// v2: monthly/lifetime plan selector — Monthly (3.99), Lifetime (24.99)
	variant_2: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showLifetimeOption: true,
		defaultPlanKey: 'lifetime',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpControl',
		lifetimePriceKey: 'proPricingExpLifetimeV1',
	},
	// v3: monthly/yearly/lifetime plan selector — Monthly (3.99), Yearly (9.99), Lifetime (24.99)
	variant_3: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showLifetimeOption: true,
		showYearlyOption: true,
		defaultPlanKey: 'lifetime',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpControl',
		yearlyPriceKey: 'proPricingExpYearlyV1',
		lifetimePriceKey: 'proPricingExpLifetimeV1',
	},
	// v4: same as v3, with updated Yearly/Lifetime pricing — Monthly (3.99), Yearly (15.99), Lifetime (39.99)
	variant_4: {
		titleKey: 'WEBAPP_PRO_MODAL_SUBHEAD',
		perks: SHARED_PERKS,
		showLifetimeOption: true,
		showYearlyOption: true,
		defaultPlanKey: 'lifetime',
		showPlanSelector: true,
		monthlyPriceKey: 'proPricingExpControl',
		yearlyPriceKey: 'proPricingExpYearlyV2',
		lifetimePriceKey: 'proPricingExpLifetimeV2',
	},
};

// Matches the tracking schema's `value` field convention (0 = web, 1 = web_mobile).
export function getTrackingDeviceValue(deviceType: Device): 0 | 1 {
	return deviceType === 'mobile' ? 1 : 0;
}

export const DEVICE_METADATA_VALUES: Record<Device, string> = {
	desktop: 'web',
	mobile: 'webmob',
	unknown: 'unknown',
};

// variant is null outside the experiment (no metadata to attach); a real value — including
// 'control' — means the checkout should carry RevenueCat/Stripe metadata for this experiment.
export function buildCheckoutMetadata(
	variant: string | null,
	deviceType: Device
): StripeCheckoutMetadataEntry[] | undefined {
	if (!variant) return undefined;

	return [
		{ key: 'device', value: DEVICE_METADATA_VALUES[deviceType] },
		{ key: 'experimentName', value: 'EXP_JW_PRO_PRICING_V2' },
		{ key: 'experimentVariant', value: variant },
	];
}
